import { ReactElement } from "react";

import { Box, BoxProps, Flex, IconProps } from "@chakra-ui/react";

import {
  CalendarWarningIcon,
  PackageBoxWarningIcon,
  QuestionDiamondIcon,
  WarningThreeIcon,
} from "ui/Icons/Icons";
import { BodyS, TitleM } from "ui/Typography/Typography";

export type SmartPickingAlertStatus = "missing" | "damaged" | "expired" | "wrong";

type StatusStyle = {
  accent: string;
  bg: string;
  Icon: (props: IconProps) => ReactElement;
};

/**
 * One visual status per issue type. The status is carried by the accent colour *and* the icon
 * shape *and* the copy, so it is never distinguishable by colour alone.
 */
const statusStyles: Record<SmartPickingAlertStatus, StatusStyle> = {
  missing: { accent: "orange.500", bg: "orange.100", Icon: QuestionDiamondIcon },
  damaged: { accent: "red.500", bg: "red.100", Icon: PackageBoxWarningIcon },
  expired: { accent: "veggie.500", bg: "veggie.100", Icon: CalendarWarningIcon },
  wrong: { accent: "purple.500", bg: "purple.100", Icon: WarningThreeIcon },
};

export type SmartPickingAlertCalloutProps = {
  status: SmartPickingAlertStatus;
  title: string;
  body: string;
  dataTestId?: string;
} & Omit<BoxProps, "title">;

export function SmartPickingAlertCallout({
  status,
  title,
  body,
  dataTestId = "smart-picking-alert-callout",
  ...rest
}: SmartPickingAlertCalloutProps) {
  const { accent, bg, Icon } = statusStyles[status];

  return (
    <Flex
      w="100%"
      bg={bg}
      borderRadius="sm"
      overflow="hidden"
      alignItems="stretch"
      data-testid={dataTestId}
      data-status={status}
      {...rest}
    >
      <Box width="4px" bg={accent} flexShrink={0} data-testid="smart-picking-alert-accent-bar" />
      <Flex flex={1} padding="s150" gap="s150" alignItems="flex-start" minWidth={0}>
        <Flex
          boxSize="32px"
          flexShrink={0}
          bg={accent}
          borderRadius="sm"
          alignItems="center"
          justifyContent="center"
          data-testid="smart-picking-alert-icon-box"
        >
          <Icon
            color="white"
            boxSize="20px"
            aria-hidden
            data-testid={`smart-picking-alert-icon-${status}`}
          />
        </Flex>
        <Box>
          <TitleM color={accent} fontFamily="Flink-Bold" fontWeight={700}>
            {title}
          </TitleM>
          <BodyS color="grey.800" marginTop="s25">
            {body}
          </BodyS>
        </Box>
      </Flex>
    </Flex>
  );
}
